import { Tab, TabList, TabPanel, TabPanels, Tabs, Tile } from '@carbon/react';
import { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import style from './entity-tabs.module.scss';

import { Aliases } from './aliases';
import { Capacities } from './capacities';
import { RunsheetSearch } from 'components/runsheet/common/runsheet-search';
import { Assets } from './assets';
import { Inventory } from './inventory';
import { Packages } from './packages';
import { CRMAccounts } from './crm-accounts';
import { EntityAssetsTab } from './entity-assets-tab';
import { MapQueryExportBanner } from 'pages/map-query/export-status-banner';
import { useMapQueryExport } from 'pages/map-query/use-map-query-export';
import { EntityDocumentsTab } from './entity-documents-tab';
import type { EntityResponse } from 'types/api-responses';
import { SlotsTab } from './slots-tab';
import { SlotAllocationsTab } from './slot-allocations-tab';
import { EntityDivisionOrders } from './division-orders';
import { EntityRoyaltyChecks } from './royalty-checks';
import { TitleSupportWorkspacesTable } from 'pages/title-support-workspaces/page';

const DEFAULT_TAB = 'documents';

/**
 * The entity page's relationship tabs. Opens on Documents unless `?tab=` names
 * another; only the selected tab renders.
 * @param {object} props The tabs.
 * @param {EntityResponse} props.entity The entity shown.
 * @return {JSX.Element} The tabs.
 */
function EntityTabs({ entity }: { entity: EntityResponse }) {
  const location = useLocation();
  const history = useHistory();
  // Held here, not by the Documents tab: only the selected tab renders, and a
  // running export must outlive switching away from it.
  const mapQueryExport = useMapQueryExport();

  // Build the tab definitions — order and content in one place
  const list: {
    key: string;
    label: string;
    tileClass: string;
    render: () => React.ReactNode;
  }[] = [
    {
      key: 'aliases',
      label: 'Aliases',
      tileClass: style.tileTab,
      render: () => <Aliases entity={entity} />,
    },
    {
      key: 'capacities',
      label: 'Capacities',
      tileClass: style.tileTab,
      render: () => <Capacities currentEntity={entity} />,
    },
    {
      key: 'interpretations',
      label: 'Interpretations',
      tileClass: style.tileRunsheetTab,
      render: () => (
        <RunsheetSearch
          searchHref={entity?.resources.queries.interpretations.href}
        />
      ),
    },
    {
      key: 'documents',
      label: 'Documents',
      tileClass: style.tileFlush,
      render: () => (
        <EntityDocumentsTab entity={entity} mapQueryExport={mapQueryExport} />
      ),
    },
    {
      key: 'assets',
      label: 'Title Assets',
      tileClass: style.tileTab,
      render: () => <Assets entity={entity} />,
    },
    {
      key: 'title-support-workspaces',
      label: 'Title Support Workspaces',
      tileClass: style.tileTab,
      render: () => <TitleSupportWorkspacesTable entityId={entity.id} />,
    },
  ];

  if (entity.hasInventory) {
    list.push(
      {
        key: 'inventory',
        label: 'Inventory',
        tileClass: style.tileTab,
        render: () => <Inventory entity={entity} />,
      },
      {
        key: 'packages',
        label: 'Packages',
        tileClass: style.tileTab,
        render: () => <Packages entity={entity} />,
      }
    );
  }

  list.push(
    {
      key: 'crm-accounts',
      label: 'CRM Accounts',
      tileClass: style.tileTab,
      render: () => <CRMAccounts entity={entity} />,
    },
    {
      // Key kept as `crm-tracts` so any deep links / bookmarks
      // (`?tab=crm-tracts`) still resolve. The visible label is the
      // unified "Assets" name; the data source is
      // the unified `GET /entities/:id/assets` endpoint that covers
      // tract + wellbore + ltd_slot.
      key: 'crm-tracts',
      label: 'Assets',
      tileClass: style.tileTab,
      render: () => <EntityAssetsTab entity={entity} />,
    },
    {
      key: 'slots',
      label: 'Slots',
      tileClass: style.tileTab,
      render: () => <SlotsTab entityId={entity.id} />,
    },
    {
      key: 'slots-allocations',
      label: 'Slots Allocations',
      tileClass: style.tileTab,
      render: () => <SlotAllocationsTab entityId={entity.id} />,
    },
    {
      key: 'division-orders',
      label: 'Division Orders',
      tileClass: style.tileTab,
      render: () => <EntityDivisionOrders entity={entity} />,
    },
    {
      key: 'royalty-checks',
      label: 'Royalty Checks',
      tileClass: style.tileTab,
      render: () => <EntityRoyaltyChecks entity={entity} />,
    }
  );

  const tabs = list;

  // Deep-link support: `?tab=<key>` picks the initial tab (e.g. the reconcile
  // tearsheet's Workspaces/Interpretations links, which open the profile in a new
  // tab on Title Assets / Interpretations). Read once at mount via a lazy
  // initializer so the correct tab paints on the first frame — no flash of tab 0,
  // and no effect that would re-apply the URL's tab (and snap the user back) after
  // an entity refetch rebuilds `tabs`. Unknown/absent keys open Documents.
  // Held by key, not index: a refetch can add or drop tabs (e.g. inventory).
  const [selectedKey, setSelectedKey] = useState(() => {
    const key = new URLSearchParams(location.search).get('tab');
    return tabs.some((tab) => tab.key === key) ? key : DEFAULT_TAB;
  });
  const keyIndex = tabs.findIndex((tab) => tab.key === selectedKey);
  const selected =
    keyIndex >= 0
      ? keyIndex
      : Math.max(
          0,
          tabs.findIndex((tab) => tab.key === DEFAULT_TAB)
        );

  return (
    <div
      className={classNames(style.tabsContained, style.anchorSection)}
      id="relationships"
    >
      <MapQueryExportBanner mapQueryExport={mapQueryExport} />
      <Tabs
        selectedIndex={selected}
        onChange={({ selectedIndex }) => {
          const { key } = tabs[selectedIndex];
          setSelectedKey(key);
          // A refetch remounts the tabs, which read ?tab= at mount. Read the
          // browser's URL, not the router's: the runsheet writes its search
          // there directly. openIC opens Create Workspace once; keeping it would
          // reopen the modal on every tab change.
          const search = new URLSearchParams(window.location.search);
          search.set('tab', key);
          search.delete('openIC');
          history.replace({ ...location, search: search.toString() });
        }}
      >
        <TabList contained>
          {tabs.map((tab) => (
            <Tab key={tab.key}>{tab.label}</Tab>
          ))}
        </TabList>
        <TabPanels>
          {tabs.map((tab, index) => (
            <TabPanel key={tab.key}>
              <Tile className={tab.tileClass}>
                {selected === index && tab.render()}
              </Tile>
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </div>
  );
}

export { EntityTabs };
